import { useCallback, useEffect, useState } from "react";
import { getAddress } from "viem";
import { SUPERNOVA_CHAIN_ID } from "./supernovaContracts";

/**
 * useSupernovaWallet — the Supernova tab's own, deliberately tiny wallet hook.
 *
 * It can do exactly three things: read which address is connected, ask the
 * member to connect one, and ask their wallet to switch to Arbitrum. It has NO
 * write path — no signing, no contract call, no approval, nothing that could
 * move an NFT or a token. That is not an oversight: this tab is a read view,
 * and staking is a member-signed transaction that ships under supervision
 * (see supernovaChain.stake). ARCrypto never holds a key and never signs.
 *
 * Separate from strategyautomation/useArbitrumWallet on purpose: that hook
 * carries the gTrade delegate-revoke write, and none of that money path belongs
 * in this bundle.
 *
 * FLAG OFF ⇒ NOTHING HAPPENS. With `enabled` false the hook never reads
 * window.ethereum, never subscribes, and never prompts — the app behaves
 * exactly as it does today.
 */

export { SUPERNOVA_CHAIN_ID };

const injectedProvider = () =>
  typeof window !== "undefined" && window.ethereum ? window.ethereum : null;

const toChecksum = (value) => {
  try {
    return getAddress(value);
  } catch {
    return null;
  }
};

export default function useSupernovaWallet({ enabled = false } = {}) {
  const [account, setAccount] = useState(null);
  const [chainId, setChainId] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [error, setError] = useState(null);

  const hasInjected = enabled && !!injectedProvider();

  /* Silent pickup of an already-authorized account. eth_accounts NEVER prompts,
     so landing on the tab can't pop a wallet dialog at a member. */
  useEffect(() => {
    if (!enabled) {
      setAccount(null);
      setChainId(null);
      setError(null);
      return undefined;
    }
    const eth = injectedProvider();
    if (!eth) return undefined;

    let live = true;
    Promise.all([
      eth.request({ method: "eth_accounts" }),
      eth.request({ method: "eth_chainId" }),
    ])
      .then(([accounts, hexChainId]) => {
        if (!live) return;
        setAccount(accounts?.length ? toChecksum(accounts[0]) : null);
        setChainId(parseInt(hexChainId, 16));
      })
      .catch(() => {
        /* A provider that won't answer eth_accounts just means "not connected".
           It is not an error worth showing anyone. */
      });

    const onAccounts = (accounts) =>
      setAccount(accounts?.length ? toChecksum(accounts[0]) : null);
    const onChain = (hexChainId) => setChainId(parseInt(hexChainId, 16));
    eth.on?.("accountsChanged", onAccounts);
    eth.on?.("chainChanged", onChain);
    return () => {
      live = false;
      eth.removeListener?.("accountsChanged", onAccounts);
      eth.removeListener?.("chainChanged", onChain);
    };
  }, [enabled]);

  const connect = useCallback(async () => {
    const eth = injectedProvider();
    if (!enabled || !eth) {
      setError(
        "No browser wallet detected. Install MetaMask, Rabby, or another EVM wallet and reload."
      );
      return null;
    }
    setConnecting(true);
    setError(null);
    try {
      const accounts = await eth.request({ method: "eth_requestAccounts" });
      const address = accounts?.length ? toChecksum(accounts[0]) : null;
      setAccount(address);
      try {
        setChainId(parseInt(await eth.request({ method: "eth_chainId" }), 16));
      } catch {
        /* Chain is a nice-to-have here; the wrong-network notice handles null. */
      }
      return address;
    } catch (e) {
      setError(e?.shortMessage || e?.message || "Wallet connection was cancelled.");
      return null;
    } finally {
      setConnecting(false);
    }
  }, [enabled]);

  const switchToArbitrum = useCallback(async () => {
    const eth = injectedProvider();
    if (!enabled || !eth) return;
    setSwitching(true);
    setError(null);
    try {
      await eth.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: `0x${SUPERNOVA_CHAIN_ID.toString(16)}` }],
      });
      setChainId(SUPERNOVA_CHAIN_ID);
    } catch (e) {
      setError(
        e?.shortMessage || e?.message || "Couldn't switch your wallet to Arbitrum One."
      );
    } finally {
      setSwitching(false);
    }
  }, [enabled]);

  return {
    account,
    chainId,
    /* With an injected provider, chain reads are routed THROUGH it, so a member
       parked on another network would get answers from the wrong chain. Until
       we know the chain, chainOk is false and the caller holds the read — an
       unread balance is recoverable, a mainnet balance shown as an Arbitrum one
       is not. Without an injected provider the read goes over the public
       Arbitrum transport, which is on the right chain by construction. */
    chainOk: !hasInjected || chainId === SUPERNOVA_CHAIN_ID,
    chainKnown: !hasInjected || chainId !== null,
    hasInjected,
    connecting,
    switching,
    error,
    clearError: () => setError(null),
    connect,
    switchToArbitrum,
  };
}
